import { registerLocaleData } from "@angular/common";

/** Loads an Angular CLDR locale-data module for the given locale id. */
export type LocaleDataLoader = (locale: string) => Promise<{ default: unknown }>;

/**
 * Explicit, statically-analyzable per-locale import map. A single dynamic
 * `import(\`@angular/common/locales/${locale}\`)` (built from a runtime
 * string) looks appealing, but bare npm specifiers built from a variable
 * aren't pre-resolvable by Vite's dev-server or the Karma test bundler —
 * both fail at runtime with "bare specifier ... not remapped to anything"
 * — so neither environment is usable for local development/testing, and
 * relying on the production esbuild build alone to "just work" would be
 * unverifiable. Each entry below is a literal, fully-static import() call
 * that every bundler (dev, test, prod) can resolve and code-split
 * identically. Covers the base language plus common region variants for a
 * broad international scientific audience; anything not listed here falls
 * back to Angular's built-in 'en-US'.
 */
const supportedLocaleLoaders: Record<string, () => Promise<{ default: unknown }>> = {
  ar: () => import("@angular/common/locales/ar"),
  cs: () => import("@angular/common/locales/cs"),
  da: () => import("@angular/common/locales/da"),
  de: () => import("@angular/common/locales/de"),
  "de-AT": () => import("@angular/common/locales/de-AT"),
  "de-CH": () => import("@angular/common/locales/de-CH"),
  el: () => import("@angular/common/locales/el"),
  en: () => import("@angular/common/locales/en"),
  "en-AU": () => import("@angular/common/locales/en-AU"),
  "en-CA": () => import("@angular/common/locales/en-CA"),
  "en-GB": () => import("@angular/common/locales/en-GB"),
  "en-IE": () => import("@angular/common/locales/en-IE"),
  "en-IN": () => import("@angular/common/locales/en-IN"),
  es: () => import("@angular/common/locales/es"),
  "es-MX": () => import("@angular/common/locales/es-MX"),
  "es-US": () => import("@angular/common/locales/es-US"),
  fi: () => import("@angular/common/locales/fi"),
  fr: () => import("@angular/common/locales/fr"),
  "fr-BE": () => import("@angular/common/locales/fr-BE"),
  "fr-CA": () => import("@angular/common/locales/fr-CA"),
  "fr-CH": () => import("@angular/common/locales/fr-CH"),
  hi: () => import("@angular/common/locales/hi"),
  hu: () => import("@angular/common/locales/hu"),
  it: () => import("@angular/common/locales/it"),
  "it-CH": () => import("@angular/common/locales/it-CH"),
  ja: () => import("@angular/common/locales/ja"),
  ko: () => import("@angular/common/locales/ko"),
  nb: () => import("@angular/common/locales/nb"),
  nl: () => import("@angular/common/locales/nl"),
  "nl-BE": () => import("@angular/common/locales/nl-BE"),
  no: () => import("@angular/common/locales/no"),
  pl: () => import("@angular/common/locales/pl"),
  pt: () => import("@angular/common/locales/pt"),
  "pt-PT": () => import("@angular/common/locales/pt-PT"),
  ru: () => import("@angular/common/locales/ru"),
  sv: () => import("@angular/common/locales/sv"),
  tr: () => import("@angular/common/locales/tr"),
  zh: () => import("@angular/common/locales/zh"),
  "zh-Hans": () => import("@angular/common/locales/zh-Hans"),
  "zh-Hant": () => import("@angular/common/locales/zh-Hant")
};

const defaultLocaleDataLoader: LocaleDataLoader = locale => {
  const loader = supportedLocaleLoaders[locale];
  return loader ? loader() : Promise.reject(new Error(`no CLDR loader registered for '${locale}'`));
};

/**
 * Detects and registers the Angular CLDR locale data matching the user's
 * browser locale, so Angular's DecimalPipe/DatePipe/CurrencyPipe format
 * numbers the same way PrimeNG's InputNumber already does (via
 * Intl.NumberFormat, which defaults to the browser's locale when no
 * explicit [locale] is set). Without this, Angular's LOCALE_ID defaults to
 * the hardcoded 'en-US', so e.g. a German-locale browser would see a
 * comma decimal separator in mass filter inputs but a period in tables
 * rendered via the `number` pipe (lipidcompass#244).
 *
 * Tries the full region-specific locale first (e.g. 'de-DE'), then falls
 * back to the base language (e.g. 'de'), then to Angular's built-in
 * 'en-US' if neither is in `supportedLocaleLoaders`.
 *
 * `loadLocaleData` is injectable so tests can supply their own loader
 * instead of exercising the real dynamic imports.
 */
export async function resolveBrowserLocaleId(
  browserLocale: string = typeof navigator !== "undefined"
    ? navigator.language
    : "en-US",
  loadLocaleData: LocaleDataLoader = defaultLocaleDataLoader
): Promise<string> {
  if (browserLocale.toLowerCase() === "en-us") {
    // Built into @angular/core, no registration needed.
    return "en-US";
  }

  const candidates = [browserLocale];
  const langCode = browserLocale.split("-")[0];
  if (langCode !== browserLocale) candidates.push(langCode);

  for (const candidate of candidates) {
    try {
      const localeModule = await loadLocaleData(candidate);
      registerLocaleData(localeModule.default as any, candidate);
      return candidate;
    } catch {
      // no CLDR data for this candidate, try the next fallback
    }
  }

  return "en-US";
}
